import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Users,
  Calendar,
  MapPin,
  Star,
  AlertTriangle,
  CheckCircle,
  Minus,
  Plus,
  Save,
  ArrowLeft,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import { api } from "@/lib/api";
import { apiRequest } from "@/lib/queryClient";
import { format } from "date-fns";
import { toast } from "@/shared/toast";
import { BatchKid, SessionLogEntry, SessionLogRequest } from "@/shared/api-types";
import { asArray, cn } from "@/lib/utils";
import { EmptyKidsState } from "@/components/coach/EmptyKidsState";

interface Batch {
  id: string;
  name: string;
  ageGroup: string;
  schedule: string | null;
  venue: string | null;
  currentEnrollment: number;
  isActive: boolean;
}

// Discipline group definitions — order determines render order
const DISCIPLINE_GROUPS = [
  {
    key: "batting",
    label: "Batting",
    attrs: [
      { field: "battingGrip" as keyof SessionLogEntry, label: "Grip" },
      { field: "battingStance" as keyof SessionLogEntry, label: "Stance" },
      { field: "battingFootwork" as keyof SessionLogEntry, label: "Footwork" },
      { field: "battingTransfer" as keyof SessionLogEntry, label: "Weight Transfer" },
      { field: "battingHead" as keyof SessionLogEntry, label: "Head Position" },
    ],
  },
  {
    key: "bowling",
    label: "Bowling",
    attrs: [
      { field: "bowlingLoad" as keyof SessionLogEntry, label: "Load Up" },
      { field: "bowlingBfc" as keyof SessionLogEntry, label: "BFC" },
      { field: "bowlingBrace" as keyof SessionLogEntry, label: "Front Leg Brace" },
      { field: "bowlingRelease" as keyof SessionLogEntry, label: "Release" },
    ],
  },
  {
    key: "fielding",
    label: "Fielding",
    attrs: [
      { field: "fieldingCatch" as keyof SessionLogEntry, label: "Catching" },
      { field: "fieldingThrow" as keyof SessionLogEntry, label: "Throwing" },
      { field: "fieldingGround" as keyof SessionLogEntry, label: "Ground Fielding" },
    ],
  },
] as const;

export default function CoachBatchEntry() {
  const [selectedBatch, setSelectedBatch] = useState<Batch | null>(null);
  const [sessionTheme, setSessionTheme] = useState("");
  const [defaultSkillRating, setDefaultSkillRating] = useState(2);
  const [exceptions, setExceptions] = useState<Map<string, Partial<SessionLogEntry>>>(new Map());
  // Tracks which discipline group is open per kid (null = all collapsed)
  const [openDisciplines, setOpenDisciplines] = useState<Map<string, string | null>>(new Map());
  // `isSubmitting` removed May 2026 — replaced with `logSessionMutation.isPending`
  // (canonical react-query state). The local boolean was racing against the
  // mutation's lifecycle and could leave the button stuck disabled or skip the
  // saving-state render entirely on fast resolutions.
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [submissionResult, setSubmissionResult] = useState<any>(null);

  const { data: batches = [] } = useQuery<Batch[]>({
    queryKey: ["/api/batches"],
    queryFn: async () => {
      const res: any = await apiRequest("GET", "/api/batches");
      return asArray<Batch>(res.data);
    },
  });

  const { data: kids = [] } = useQuery<BatchKid[]>({
    queryKey: ["/api/batches", selectedBatch?.id, "kids"],
    queryFn: async () => {
      const res: any = await apiRequest("GET", `/api/batches/${selectedBatch!.id}/kids`);
      return asArray<BatchKid>(res.data);
    },
    enabled: !!selectedBatch,
  });

  const logSessionMutation = useMutation({
    mutationFn: async (data: SessionLogRequest) => {
      const response = await api.post("/api/sessions/log", data);
      return response.data;
    },
    onSuccess: (result) => {
      setSubmissionResult(result);
      setShowConfirmation(true);
      // @/shared/toast exports `toast` as a function, not an object with
      // .success / .error methods. Fixed May 2026 — calling toast.success /
      // toast.error here was throwing "toast.error is not a function" at
      // submit time and silently swallowing both success + failure feedback.
      toast({
        title: "Session logged successfully",
        description: `${result.logsCreated} entries created.`,
      });
    },
    onError: (error) => {
      const message = error instanceof Error ? error.message : "Please try again.";
      toast({
        title: "Failed to log session",
        description: message,
      });
      // Useful diagnostic logging — captures the message and the failing
      // request shape so a CI failure log shows *why* the API rejected,
      // not just "confirmation screen not found". Avoid logging secrets:
      // body fields are limited to non-PII identifiers + counts.
      console.error("[CoachBatchEntry] /api/sessions/log failed:", {
        message,
        batchId: selectedBatch?.id,
        exceptionsCount: exceptions.size,
        defaultSkillRating,
      });
    },
    // No onSettled — `logSessionMutation.isPending` is canonical state and
    // resets automatically when the mutation finishes (success or error).
  });

  const handleKidUpdate = (kidId: string, field: keyof SessionLogEntry, value: any) => {
    setExceptions(prev => {
      const newExceptions = new Map(prev);
      const existing = newExceptions.get(kidId) || {};
      newExceptions.set(kidId, { ...existing, [field]: value });
      return newExceptions;
    });
  };

  const getKidValue = (kidId: string, field: keyof SessionLogEntry) => {
    const exception = exceptions.get(kidId);
    if (exception && field in exception) {
      return exception[field];
    }
    
    // Return defaults
    switch (field) {
      case 'attendance': return true;
      case 'skillRating': return defaultSkillRating;
      case 'oversBowled': return 0;
      case 'painFlag': return false;
      case 'painNotes': return '';
      case 'individualNotes': return '';
      default: return undefined;
    }
  };

  const toggleDiscipline = (kidId: string, discipline: string) => {
    setOpenDisciplines(prev => {
      const next = new Map(prev);
      next.set(kidId, prev.get(kidId) === discipline ? null : discipline);
      return next;
    });
  };

  const handleSubmit = () => {
    if (!selectedBatch) return;

    const today = format(new Date(), "yyyy-MM-dd");
    const sessionId = `batch-${selectedBatch.id}-${today}`;

    const sessionLogData: SessionLogRequest = {
      sessionId,
      batchId: String(selectedBatch.id),
      defaultSkillRating,
      theme: sessionTheme || "General Practice",
      exceptions: Array.from(exceptions.entries()).map(([kidId, data]) => ({
        kidId,
        attendance: data.attendance ?? true,
        skillRating: data.skillRating ?? defaultSkillRating,
        oversBowled: data.oversBowled ?? 0,
        painFlag: data.painFlag ?? false,
        painNotes: data.painNotes || '',
        individualNotes: data.individualNotes || '',
        battingGrip: data.battingGrip,
        battingStance: data.battingStance,
        battingFootwork: data.battingFootwork,
        battingTransfer: data.battingTransfer,
        battingHead: data.battingHead,
        bowlingLoad: data.bowlingLoad,
        bowlingBfc: data.bowlingBfc,
        bowlingBrace: data.bowlingBrace,
        bowlingRelease: data.bowlingRelease,
        fieldingCatch: data.fieldingCatch,
        fieldingThrow: data.fieldingThrow,
        fieldingGround: data.fieldingGround,
      })),
      idempotencyKey: `${sessionId}-${Date.now()}`,
    };

    logSessionMutation.mutate(sessionLogData);
  };

  // 1-5 stepper for a single skill sub-attribute.
  // undefined = not recorded; + from undefined starts at 1; − from 1 clears to undefined.
  const renderSkillStepper = (kidId: string, field: keyof SessionLogEntry) => {
    const value = getKidValue(kidId, field) as number | undefined;
    return (
      <div className="flex items-center gap-1.5">
        <button
          type="button"
          className="w-9 h-9 rounded-lg border-2 border-gray-200 bg-gray-50 hover:bg-gray-100 flex items-center justify-center touch-manipulation disabled:opacity-30"
          onClick={() => handleKidUpdate(kidId, field, value === 1 ? undefined : Math.max(1, (value ?? 1) - 1))}
          disabled={!value}
          data-testid={`skill-${field}-minus-${kidId}`}
        >
          <Minus className="w-3 h-3" />
        </button>
        <span className="w-7 text-center font-semibold text-sm tabular-nums" data-testid={`skill-${field}-value-${kidId}`}>
          {value ?? '—'}
        </span>
        <button
          type="button"
          className="w-9 h-9 rounded-lg border-2 border-gray-200 bg-gray-50 hover:bg-gray-100 flex items-center justify-center touch-manipulation disabled:opacity-30"
          onClick={() => handleKidUpdate(kidId, field, Math.min(5, (value ?? 0) + 1))}
          disabled={value === 5}
          data-testid={`skill-${field}-plus-${kidId}`}
        >
          <Plus className="w-3 h-3" />
        </button>
      </div>
    );
  };

  const renderDisciplineGroups = (kidId: string) => {
    const open = openDisciplines.get(kidId) ?? null;
    return (
      <div className="space-y-2" data-testid={`discipline-groups-${kidId}`}>
        {DISCIPLINE_GROUPS.map((group) => {
          const isOpen = open === group.key;
          return (
            <div key={group.key} className="border rounded-lg overflow-hidden">
              <button
                type="button"
                className="w-full flex items-center justify-between px-3 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                onClick={() => toggleDiscipline(kidId, group.key)}
                data-testid={`discipline-toggle-${group.key}-${kidId}`}
              >
                <span>{group.label}</span>
                {isOpen
                  ? <ChevronDown className="w-4 h-4 text-gray-400" />
                  : <ChevronRight className="w-4 h-4 text-gray-400" />
                }
              </button>
              {isOpen && (
                <div className="px-3 pb-3 pt-1 grid grid-cols-1 sm:grid-cols-2 gap-3 bg-gray-50/50">
                  {group.attrs.map(({ field, label }) => (
                    <div key={field} className="flex items-center justify-between gap-2">
                      <span className="text-xs text-gray-600 flex-1">{label}</span>
                      {renderSkillStepper(kidId, field)}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const renderStarRating = (kidId: string) => {
    const currentRating = getKidValue(kidId, 'skillRating') as number;
    
    return (
      <div className="flex gap-1">
        {[1, 2, 3].map((rating) => (
          <button
            key={rating}
            type="button"
            className={cn(
              "w-11 h-11 rounded-lg border-2 flex items-center justify-center transition-colors",
              "touch-manipulation", // Ensures 44px+ touch target
              currentRating === rating
                ? "bg-yellow-100 border-yellow-400 text-yellow-600"
                : "bg-gray-50 border-gray-200 text-gray-400 hover:bg-gray-100"
            )}
            onClick={() => handleKidUpdate(kidId, 'skillRating', rating)}
            data-testid={`skill-rating-${kidId}-${rating}`}
          >
            <Star className="w-5 h-5 fill-current" />
          </button>
        ))}
      </div>
    );
  };

  const renderOversStepper = (kidId: string) => {
    const currentOvers = getKidValue(kidId, 'oversBowled') as number;
    
    return (
      <div className="flex items-center gap-2">
        <button
          type="button"
          className="w-11 h-11 rounded-lg border-2 border-gray-200 bg-gray-50 hover:bg-gray-100 flex items-center justify-center touch-manipulation"
          onClick={() => handleKidUpdate(kidId, 'oversBowled', Math.max(0, currentOvers - 0.5))}
          data-testid={`overs-minus-${kidId}`}
        >
          <Minus className="w-4 h-4" />
        </button>
        <span className="w-12 text-center font-semibold text-lg" data-testid={`overs-value-${kidId}`}>
          {currentOvers}
        </span>
        <button
          type="button"
          className="w-11 h-11 rounded-lg border-2 border-gray-200 bg-gray-50 hover:bg-gray-100 flex items-center justify-center touch-manipulation"
          onClick={() => handleKidUpdate(kidId, 'oversBowled', currentOvers + 0.5)}
          data-testid={`overs-plus-${kidId}`}
        >
          <Plus className="w-4 h-4" />
        </button>
      </div>
    );
  };

  if (showConfirmation && submissionResult) {
    return (
      <div className="container mx-auto p-4 sm:p-6 space-y-6 max-w-4xl" data-testid="coach-batch-entry-confirmation">
        <Card>
          <CardHeader className="text-center">
            <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <CardTitle className="text-2xl text-green-800">Session Logged Successfully!</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Summary */}
            <div className="bg-green-50 rounded-lg p-4 space-y-2">
              <h3 className="font-semibold text-green-800">Summary</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
                <div>
                  <span className="text-green-600 font-semibold">{submissionResult.logsCreated}</span>
                  <span className="text-green-700"> entries created</span>
                </div>
                <div>
                  <span className="text-green-600 font-semibold">{submissionResult.homeworkAssigned}</span>
                  <span className="text-green-700"> homework assigned</span>
                </div>
                <div>
                  <span className="text-green-600 font-semibold">{submissionResult.parentNotifications}</span>
                  <span className="text-green-700"> parents notified</span>
                </div>
              </div>
            </div>

            {/* Follow-ups */}
            <div className="space-y-3">
              <h3 className="font-semibold text-gray-900">Follow-ups</h3>
              <div className="space-y-2 text-sm text-gray-600">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>Parents will receive session summary within 1 hour</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>Homework assignments sent to kids with ratings below 2 stars</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>Pain/niggle flags forwarded to academy management</span>
                </div>
              </div>
            </div>

            {/* Next Session CTA */}
            <div className="bg-blue-50 rounded-lg p-4">
              <h3 className="font-semibold text-blue-800 mb-2">Next Session</h3>
              <p className="text-sm text-blue-700 mb-3">
                Next {selectedBatch?.name} session: Tuesday, Dec 31 at 6:00 PM
              </p>
              <Button 
                className="w-full sm:w-auto"
                onClick={() => {
                  setShowConfirmation(false);
                  setSelectedBatch(null);
                  setSessionTheme("");
                  setExceptions(new Map());
                  setSubmissionResult(null);
                }}
                data-testid="btn-log-another-session"
              >
                Log Another Session
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4 sm:p-6 space-y-6 max-w-6xl" data-testid="coach-batch-entry-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">
          Coach Batch Entry
        </h1>
        <p className="text-slate-600 mt-1 text-sm sm:text-base">
          Log session data for your batch in under 30 seconds
        </p>
      </div>

      {!selectedBatch ? (
        /* Batch Selection */
        <div className="space-y-4">
          <h2 className="text-xl font-semibold text-gray-900">Select Today's Batch</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="batch-selection-grid">
            {batches.map((batch) => (
              <Card 
                key={batch.id} 
                className="cursor-pointer hover:shadow-md transition-shadow"
                onClick={() => setSelectedBatch(batch)}
                data-testid={`batch-card-${batch.id}`}
              >
                <CardContent className="p-4 space-y-3">
                  <div>
                    <h3 className="font-bold text-lg text-gray-900">{batch.name}</h3>
                    <Badge variant="secondary" className="mt-1">{batch.ageGroup}</Badge>
                  </div>
                  <div className="space-y-2 text-sm text-gray-600">
                    {batch.schedule && (
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4" />
                        <span>{batch.schedule}</span>
                      </div>
                    )}
                    {batch.venue && (
                      <div className="flex items-center gap-2">
                        <MapPin className="h-4 w-4" />
                        <span>{batch.venue}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <Users className="h-4 w-4" />
                      <span className="font-semibold text-gray-900">{batch.currentEnrollment} kids</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      ) : (
        /* Session Logging Interface */
        <div className="space-y-6">
          {/* Session Header */}
          <Card>
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="space-y-2">
                  <h2 className="text-xl font-bold text-gray-900">{selectedBatch.name}</h2>
                  <Badge variant="secondary">{selectedBatch.ageGroup}</Badge>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelectedBatch(null)}
                  data-testid="btn-back-to-batches"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
              </div>
              
              <div className="space-y-3 text-sm text-gray-600">
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  <span>{format(new Date(), "EEEE, MMM dd, yyyy")}</span>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Today's Theme (optional)
                  </label>
                  <Input
                    placeholder="e.g. Batting Balance Drills"
                    value={sessionTheme}
                    onChange={(e) => setSessionTheme(e.target.value)}
                    data-testid="session-theme-input"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Bulk Defaults */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Bulk Defaults</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Default Skill Rating (applies to all kids)
                </label>
                <div className="flex gap-1">
                  {[1, 2, 3].map((rating) => (
                    <button
                      key={rating}
                      type="button"
                      className={cn(
                        "w-12 h-12 rounded-lg border-2 flex items-center justify-center transition-colors",
                        defaultSkillRating === rating
                          ? "bg-yellow-100 border-yellow-400 text-yellow-600"
                          : "bg-gray-50 border-gray-200 text-gray-400 hover:bg-gray-100"
                      )}
                      onClick={() => setDefaultSkillRating(rating)}
                      data-testid={`default-skill-rating-${rating}`}
                    >
                      <Star className="w-5 h-5 fill-current" />
                    </button>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Kids List */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Kids in Batch ({kids.length})</CardTitle>
              <p className="text-sm text-gray-600">Edit only the exceptions - defaults apply to everyone else</p>
            </CardHeader>
            <CardContent className="space-y-4">
              {kids.length === 0
                ? <EmptyKidsState />
                : kids.map((kid) => {
                  const painFlag = getKidValue(kid.id, 'painFlag') as boolean;
                  const attendance = getKidValue(kid.id, 'attendance') as boolean;
                
                return (
                  <div 
                    key={kid.id} 
                    className={cn(
                      "border rounded-lg p-4 space-y-4",
                      !attendance && "bg-gray-50 opacity-75"
                    )}
                    data-testid={`kid-card-${kid.id}`}
                  >
                    {/* Kid Header */}
                    <div className="flex items-start justify-between">
                      <div className="space-y-1">
                        <h3 className="font-semibold text-gray-900">{kid.fullName}</h3>
                        <p className="text-sm text-gray-600">Age {kid.age} • Parent: {kid.parentName}</p>
                        {kid.recentPerformance && (
                          <div className="flex items-center gap-3 text-xs text-gray-500">
                            <span>Avg: {kid.recentPerformance.avgRating.toFixed(1)}★</span>
                            <span>Attendance: {kid.recentPerformance.attendanceRate}%</span>
                          </div>
                        )}
                      </div>
                      
                      {/* Attendance Toggle */}
                      <div className="flex items-center gap-2">
                        <label className="text-sm font-medium text-gray-700">Present</label>
                        <Switch
                          checked={attendance}
                          onCheckedChange={(checked) => handleKidUpdate(kid.id, 'attendance', checked)}
                          data-testid={`attendance-${kid.id}`}
                        />
                      </div>
                    </div>

                    {attendance && (
                      <>
                        {/* Pain/Niggle Flag - Always Visible */}
                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center gap-2">
                            <AlertTriangle className={cn("w-5 h-5", painFlag ? "text-red-500" : "text-gray-400")} />
                            <span className="font-medium text-gray-900">Pain/Niggle Flag</span>
                            {painFlag && <span className="text-2xl">🚩</span>}
                          </div>
                          <Switch
                            checked={painFlag}
                            onCheckedChange={(checked) => handleKidUpdate(kid.id, 'painFlag', checked)}
                            data-testid={`pain-flag-${kid.id}`}
                          />
                        </div>

                        {painFlag && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Pain/Niggle Notes (Optional)
                            </label>
                            <Textarea
                              placeholder="Describe the pain or niggle..."
                              value={getKidValue(kid.id, 'painNotes') as string}
                              onChange={(e) => handleKidUpdate(kid.id, 'painNotes', e.target.value)}
                              className="min-h-[80px]"
                              data-testid={`pain-notes-${kid.id}`}
                            />
                          </div>
                        )}

                        {/* Performance Controls */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          {/* Skill Rating — overall composite, bulk default applies */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Skill Rating
                            </label>
                            {renderStarRating(kid.id)}
                          </div>

                          {/* Overs Bowled — untouched */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Overs Bowled
                            </label>
                            {renderOversStepper(kid.id)}
                          </div>
                        </div>

                        {/* Discipline Groups — collapsed by default */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Skill Detail (optional)
                          </label>
                          {renderDisciplineGroups(kid.id)}
                        </div>

                        {/* Individual Notes */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Individual Notes (Optional)
                          </label>
                          <Textarea
                            placeholder="Any specific notes for this kid..."
                            value={getKidValue(kid.id, 'individualNotes') as string}
                            onChange={(e) => handleKidUpdate(kid.id, 'individualNotes', e.target.value)}
                            className="min-h-[80px]"
                            data-testid={`individual-notes-${kid.id}`}
                          />
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </CardContent>
          </Card>

          {/* Submit Button */}
          <Card>
            <CardContent className="p-4 sm:p-6">
              <Button
                onClick={handleSubmit}
                disabled={logSessionMutation.isPending}
                className="w-full sm:w-auto min-w-[200px]"
                size="lg"
                data-testid="btn-log-session"
              >
                {logSessionMutation.isPending ? (
                  <>
                    <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Log Session
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}